import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConnectAccountingSoftware } from 'shared/accountingSoftware';
import { useAlertDialog } from 'shared';
import { useClientData } from '@/providers/ClientData.provider';
import { getRequestedRailzService } from './railzServices';

// The dev-server port Railz redirects back to when the app runs on a
// portless localhost URL.
const LOCAL_DEV_FALLBACK_PORT = '3000';

// The new (ASP) connection & GL setup lives on orphan pages under /accounting
// while the API is validated (ID-13335); the current Railz page keeps
// /integrations/accounting-software.
const SETTINGS_PATH = '/accounting/integrations/accounting-software/';

// Portal host for the shared connect/setup flow: supplies the Railz widget
// inputs and owns the post-save routes.
export default function AccountingSoftwarePage() {
  const history = useHistory();
  const { response } = useClientData();
  const alertDialog = useAlertDialog();
  // An ERP card links here with ?service=<railz service>; the connect widget is then scoped to it
  const requestedService = getRequestedRailzService(useLocation().search);

  const { t } = useTranslation('translations', {
    keyPrefix: 'Shared.AccountingSystem.AccountingSoftware.ChartOfAccount.Dialogs.UpdateSuccess',
  });

  const { protocol, port, hostname } = window.location;
  const currentPort = port || LOCAL_DEV_FALLBACK_PORT;
  const isLocalHost = hostname === 'localhost' || hostname === 'bs-local.com';
  const origin = isLocalHost ? `${protocol}//${hostname}:${currentPort}` : `${protocol}//${hostname}`;
  const settingsURL = `${origin}${SETTINGS_PATH}`;

  // Keep the ERP scope when Railz sends the user back here after connecting...
  const redirectURL = requestedService ? `${settingsURL}?service=${requestedService}` : settingsURL;
  // ...but never on close: returning with ?service= re-opens the widget immediately, so there is
  // no way out of the connect flow. Closing a scoped widget goes back to the marketplace instead.
  const closeRedirectURL = requestedService ? `${origin}/integrations` : settingsURL;

  return (
    <ConnectAccountingSoftware
      railzWidgetId={import.meta.env.VITE_RAILZ_WIDGET_ID || ''}
      businessName={response.AccountID}
      redirectURL={redirectURL}
      closeRedirectURL={closeRedirectURL}
      service={requestedService}
      onCompleteChartOfAccount={async (receivableName, payableName) => {
        const result = await alertDialog.success(
          t('title', { defaultValue: 'Accounting System Details Updated' }),
          t('subTitle', {
            defaultValue:
              'Your chart account for {{payableName}} and {{receivableName}} has been connected and updated.',
            payableName,
            receivableName,
          }),
          {
            confirmLabel: t('confirmButton', { defaultValue: 'Go to Accounting System' }),
            abortLabel: t('cancelButton', { defaultValue: 'Back to Integrations' }),
          },
        );

        history.push(result === 'confirm' ? '/accounting/payables' : '/integrations');
      }}
    />
  );
}
